import { Injectable } from "@angular/core";
import {
  ApiCategorieEis,
  ApiCursusInfoVoorIndienen,
  ApiExamenonderdeelInfoVoorIndienen,
  ApiIndienenVoorstelPayload,
  ApiInfoVoorIndienen,
  ApiInfoVoorIndienenMinorExamenonderdeel,
  ApiMinorInfoVoorIndienen,
  ApiProfileringCursusPayload,
  ApiProfileringInfoVoorIndienen,
  ApiProfileringMinorExamenonderdeelPayload,
  ApiProfileringMinorPayload,
  ApiVoorstelDocumentPayload,
} from "../api/model/api-indienen-voorstel";
import { ApiHelper } from "../domain/helpers/api-helper";
import {
  CategoryRequirement,
  FileToUpload,
  SubmitInfo,
  SubmitInfoCourse,
  SubmitInfoElectives,
  SubmitInfoMinor,
  SubmitInfoMinorExamComponent,
  SubmitInfoOtherExamComponent,
} from "../domain/submit-proposal";

@Injectable({
  providedIn: "root",
})
export class SubmitProposalMappingService {
  mapSubmitInfo(apiInfoVoorIndienen: ApiInfoVoorIndienen): SubmitInfo {
    return {
      planId: apiInfoVoorIndienen.spla_id,
      pointsPlannedOutsideElectives: apiInfoVoorIndienen.studiepunten_gepland_exclusief_profilering,
      textSubmitProposal: apiInfoVoorIndienen.tekst_indienen_voorstel?.trim(),
      textConfirmProposal: apiInfoVoorIndienen.tekst_bevestig_voorstel?.trim(),
      textConfirmUpload: apiInfoVoorIndienen.tekst_bevestig_document_upload?.trim(),
      maxDocumentsToSubmit: parseInt(apiInfoVoorIndienen.max_documenten_indienen),
      electives: this.mapSubmitInfoElectives(apiInfoVoorIndienen.profilering),
      otherExamComponents: this.mapSubmitInfoOtherExamComponents(apiInfoVoorIndienen.overige_examenonderdelen),
    };
  }

  private mapSubmitInfoElectives(apiProfileringInfoVoorIndienen: ApiProfileringInfoVoorIndienen): SubmitInfoElectives {
    return {
      examComponentCode: apiProfileringInfoVoorIndienen.code,
      examComponentName: apiProfileringInfoVoorIndienen.naam,
      minorRequirementType: apiProfileringInfoVoorIndienen.minor_eis_type,
      minorRequirementTypeDescription: apiProfileringInfoVoorIndienen.minor_eis_type_oms,
      minimumPoints: apiProfileringInfoVoorIndienen.minimum_punten,
      maximumPoints: apiProfileringInfoVoorIndienen.maximum_punten,
      categoryRequirements:
        apiProfileringInfoVoorIndienen.categorie_eisen?.map((eis) => this.mapCategoryRequirement(eis)) ?? [],
      individualArrangements:
        apiProfileringInfoVoorIndienen.individuele_regelingen?.map((cursus) => this.mapSubmitInfoCourse(cursus)) ?? [],
      minors: apiProfileringInfoVoorIndienen.minors?.map((minor) => this.mapMinor(minor)) ?? [],
      courses: apiProfileringInfoVoorIndienen.losse_cursussen?.map((cursus) => this.mapSubmitInfoCourse(cursus)) ?? [],
    };
  }

  private mapSubmitInfoCourse(apiInfoVoorIndienenCursus: ApiCursusInfoVoorIndienen): SubmitInfoCourse {
    return {
      id: apiInfoVoorIndienenCursus.spcu_id,
      code: apiInfoVoorIndienenCursus.cursus,
      name: apiInfoVoorIndienenCursus.korte_naam,
      customCourseName: apiInfoVoorIndienenCursus.eigen_cursus,
      customCoursePoints: apiInfoVoorIndienenCursus.eigen_cursus_punten,
      studyPoints: apiInfoVoorIndienenCursus.studiepunten,
      categoryCode: apiInfoVoorIndienenCursus.categorie ?? "",
      categoryDescription: apiInfoVoorIndienenCursus.categorie_oms ?? "",
      statusObtained: ApiHelper.caciBooleanToBooleanOrUndefined(apiInfoVoorIndienenCursus.status_behaald),
      isEnrolled: ApiHelper.caciBooleanToBoolean(apiInfoVoorIndienenCursus.status_ingeschreven),
      enrolledPeriods:
        apiInfoVoorIndienenCursus.status_studiejaar != null && apiInfoVoorIndienenCursus.status_periode != null
          ? [
              {
                yearNr: apiInfoVoorIndienenCursus.status_studiejaar,
                periodNr: apiInfoVoorIndienenCursus.status_periode,
              },
            ]
          : [],
      isInPlanLater: ApiHelper.caciBooleanToBoolean(apiInfoVoorIndienenCursus.plan_later),
      labelColor: apiInfoVoorIndienenCursus.kleur,
      replaceOld: apiInfoVoorIndienenCursus.vervanging_oud,
      replaceNew: apiInfoVoorIndienenCursus.vervanging_nieuw,
    };
  }

  private mapCategoryRequirement(categorieEis: ApiCategorieEis): CategoryRequirement {
    return {
      categoryCode: categorieEis.categorie,
      categoryDescription: categorieEis.categorie_oms,
    };
  }

  private mapMinor(apiMinor: ApiMinorInfoVoorIndienen): SubmitInfoMinor {
    return {
      code: apiMinor.minor,
      name: apiMinor.naam,
      type: apiMinor.type,
      examComponents: apiMinor.examenonderdelen.map((comp) => this.mapMinorExamComponent(comp)),
      studyProgram: apiMinor.minor_examenprogramma,
      minimumPoints: apiMinor.minimum_punten,
    };
  }

  private mapMinorExamComponent(
    apiMinorExamenonderdeel: ApiInfoVoorIndienenMinorExamenonderdeel,
  ): SubmitInfoMinorExamComponent {
    const minorExamComponent: SubmitInfoMinorExamComponent = {
      id: apiMinorExamenonderdeel.sple_id,
      name: apiMinorExamenonderdeel.minor_onderdeel_naam,
      courses: apiMinorExamenonderdeel.cursussen.map((cursus) => this.mapSubmitInfoCourse(cursus)),
    };
    return minorExamComponent;
  }

  private mapSubmitInfoOtherExamComponents(
    apiExamenonderdelen: ApiExamenonderdeelInfoVoorIndienen[],
  ): SubmitInfoOtherExamComponent[] {
    return apiExamenonderdelen.map((apiExamenonderdeel) => ({
      code: apiExamenonderdeel.examenonderdeel,
      name: apiExamenonderdeel.examenonderdeel_naam,
      specialisationName: apiExamenonderdeel.specialisatie_naam,
      courses: apiExamenonderdeel.cursussen.map((cursus) => this.mapSubmitInfoCourse(cursus)),
    }));
  }

  mapToApiIndienenVoorstelPayload(
    submitInfo: SubmitInfo,
    filesToUpload: FileToUpload[],
    explanation: string,
  ): ApiIndienenVoorstelPayload {
    return {
      spla_id: submitInfo.planId,
      toelichting_indienen: explanation,
      individuele_regelingen:
        submitInfo.electives.individualArrangements?.map((course) => this.mapToApiProfileringCursusPayload(course)) ??
        [],
      minors: submitInfo.electives.minors?.map((minor) => this.mapToApiMinor(minor)) ?? [],
      losse_cursussen:
        submitInfo.electives.courses?.map((course) => this.mapToApiProfileringCursusPayload(course)) ?? [],
      documenten: filesToUpload?.map((fileToUpload) => this.mapToApiProfileringDocumentPayload(fileToUpload)) ?? [],
    };
  }

  private mapToApiMinor(minor: SubmitInfoMinor): ApiProfileringMinorPayload {
    return {
      minor: minor.code,
      minor_examenprogramma: minor.studyProgram,
      examenonderdelen: minor.examComponents.map((comp) => this.mapToApiMinorExamenonderdeel(comp)),
    };
  }

  private mapToApiMinorExamenonderdeel(
    component: SubmitInfoMinorExamComponent,
  ): ApiProfileringMinorExamenonderdeelPayload {
    return {
      sple_id: component.id,
      cursussen: component.courses.map((course) => this.mapToApiProfileringCursusPayload(course)),
    };
  }

  private mapToApiProfileringCursusPayload(course: SubmitInfoCourse): ApiProfileringCursusPayload {
    return {
      spcu_id: course.id,
    };
  }

  private mapToApiProfileringDocumentPayload(fileToUpload: FileToUpload): ApiVoorstelDocumentPayload {
    return {
      bestandsnaam: fileToUpload.file.name,
      document_grootte: fileToUpload.file.size,
    };
  }
}
